import React, {useEffect} from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { upgradeFirmware } from "../../../redux/actions/firmware.actions";
import { UpdateStatusLabels } from "../../../mqttData/updateStatus/updateStatusLabels.enums";
import ModalShell, { ModalLoading, ModalTone } from "./ModalShell";

interface ModalProps {
    isOpen: boolean,
    setOpen: any,
    id: string,
    firmwareType: string,
    version: string
}

interface ModalView {
    title: React.ReactNode;
    tone?: ModalTone;
    body: React.ReactNode;
    /** Offer "Update anyway" next to Close */
    canContinue?: boolean;
    loading?: boolean;
}

const SingleUpdateModal: React.FC<ModalProps> = ({isOpen, setOpen,id,firmwareType,version}) => {
    const dispatch: AppDispatch = useDispatch();
    const {data: upgradeData, loading, error} = useSelector((state: RootState) => state.firmwareUpgrade);
    const {data: updateStatus} = useSelector((state: RootState) => state.updateStatus);

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (isOpen) {
            const sendUpdateData = async (id: string) => {
                await dispatch(
                    upgradeFirmware({spa_id: id, platform: firmwareType, version: version})
                );
            }
            sendUpdateData(id);
        }
    }, [dispatch, id, isOpen, firmwareType, version]);

    const handleContinue = async () => {
        await dispatch(
            upgradeFirmware({spa_id: id, platform: firmwareType, version: version, ignoreSame:true})
        );
    };


    const getUpdateInfo = () => {
        if (updateStatus.spa_id === undefined || updateStatus.spa_id !== id) {
            return "No Update Data Available";
        }
        return `${updateStatus.type} version: ${updateStatus.version}`;
    }

    const shouldShowProgress = () => {
        return updateStatus?.spa_id !== undefined && updateStatus?.spa_id === id;
    }

    const sameVersionView = (): ModalView => ({
        title: "Already on this version",
        tone: "warning",
        canContinue: true,
        body: <>
            {upgradeData?.message && <div className="dlg-notice warning">{upgradeData.message}</div>}
            <p>Your spa is already on the specified firmware version. Would you like to update anyway?</p>
        </>,
    });

    const getView = (): ModalView => {
        if (loading) {
            return { title: "Requesting update…", body: <ModalLoading/>, loading: true };
        }

        // Firmware upgrade request failed
        if (error) {
            if (typeof error === "string" && error.includes("already on")) {
                return sameVersionView();
            }
            return { title: "An error occurred", tone: "error", body: <p>{error}</p> };
        }

        // Spa is being downgraded and moved to beta
        if (firmwareType === "yocto" && version < "1.0.45") {
            return {
                title: "Spa moved to beta",
                tone: "info",
                body: <p>This process may take up to 10 minutes to complete.</p>,
            };
        }

        // LPC or SpaBoy update while another update is in progress
        if (updateStatus?.message === 'rejected') {
            return { title: "Request rejected", tone: "error", body: <p>Please try again in 5-10 minutes.</p> };
        }

        // Update to the LPC while the LPC is busy updating the SpaBoy
        if (updateStatus?.message === 'busy') {
            return {
                title: "Update queued",
                tone: "info",
                body: <p>The LPC is busy at the moment. Your update will begin automatically in 2 minutes.</p>,
            };
        }

        // Regular update process
        if (shouldShowProgress() && (updateStatus?.progress ?? 0) < 100) {
            const progress = updateStatus?.progress ?? 0;
            return {
                title: <span style={{textTransform: "capitalize"}}>
                    {updateStatus?.message === "update_status" ? "Installing…" : (updateStatus?.message || "Updating…")}
                </span>,
                body: <>
                    <p style={{textTransform: "capitalize"}}>{getUpdateInfo()}</p>
                    <div className="dlg-progress">
                        <div className="dlg-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                            <div className="dlg-progress-bar" style={{width: `${progress}%`}}/>
                        </div>
                        <span className="dlg-progress-value">{progress}%</span>
                    </div>
                    <p className="dlg-muted">You can close this window; progress also shows on the spa page.</p>
                </>,
            };
        }

        if (shouldShowProgress() && (updateStatus?.progress ?? 0) === 100) {
            const isSuccess = updateStatus?.[UpdateStatusLabels.success] === true;
            const isUpdateStatus = updateStatus?.message === 'update_status';
            return {
                title: isSuccess ? (isUpdateStatus ? 'Updated successfully' : 'Download complete') : 'Update failed',
                tone: isSuccess ? "success" : "error",
                body: <p>
                    {!isSuccess ? "The update process encountered an error."
                        : isUpdateStatus ? "Update completed successfully."
                        : "Update will begin shortly."}
                </p>,
            };
        }

        // Prompt to overwrite only if message is all firmware is same version
        if (typeof upgradeData?.message === "string" && upgradeData.message.includes("already on") && !upgradeData.message.includes("Skipped:")) {
            return sameVersionView();
        }

        return {
            title: "Update will begin shortly",
            tone: "info",
            body: upgradeData?.message ? <p>{upgradeData.message}</p> : null,
        };
    };

    const view = getView();

    return (
        <ModalShell
            open={isOpen}
            onClose={handleClose}
            size="sm"
            title={view.title}
            tone={view.tone}
            actions={view.loading ? undefined : view.canContinue ?
                <>
                    <button className="btn" onClick={handleClose}>Close</button>
                    <button className="btn btn-primary" onClick={handleContinue}>Update anyway</button>
                </>
                :
                <button className="btn btn-primary" onClick={handleClose}>{view.tone ? 'OK' : 'Close'}</button>
            }
        >
            {view.body}
        </ModalShell>
    );
}
export default SingleUpdateModal;
